"use client";

import { usePathname } from "next/navigation";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";
import SustainabilitySection from "./SustainabilitySection";
import JsonLd from "@/app/components/JsonLd";

interface PoliciesProps {
  data: {
    id: number;
    title: string;
    content: string;
    useH1Tag?: boolean;
  };
}

/* --- Schemas --- */
const merchantReturnPolicySchema = {
  "@context": "https://schema.org",
  "@type": "MerchantReturnPolicy",
  name: "CrunchyFashion Return Policy",
  applicableCountry: "IN",
  returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
  merchantReturnDays: 7,
  returnMethod: "https://schema.org/ReturnByMail",
  returnFees: "https://schema.org/FreeReturn",
  refundType: "https://schema.org/FullRefund",
};

const shippingPolicySchema = {
  "@context": "https://schema.org",
  "@type": "OfferShippingDetails",
  shippingDestination: {
    "@type": "DefinedRegion",
    addressCountry: { "@type": "Country", name: "IN" },
  },
  deliveryTime: {
    "@type": "ShippingDeliveryTime",
    handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 2, unitCode: "DAY" },
    transitTime: { "@type": "QuantitativeValue", minValue: 3, maxValue: 7, unitCode: "DAY" },
  },
  shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "INR" },
};

export default function Policies({ data }: PoliciesProps) {
  const pathname = usePathname();

  if (pathname === "/sustainability") {
    return <SustainabilitySection />;
  }

  // Two-tone title logic
  const titleParts = data?.title?.split(" ") || [];
  const firstPart = titleParts[0] || "";
  const restPart = titleParts.slice(1).join(" ") || "";

  return (
    <>
      <JsonLd schema={merchantReturnPolicySchema} />
      <JsonLd schema={shippingPolicySchema} />

      <section className="bg-white py-20 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          
          {/* --- ALIGNED HEADER SECTION --- */}
          <motion.header 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 border-l-4 border-black pl-6 md:pl-10"
          >
            <div className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none mb-4">
              {data?.useH1Tag ? (
                <h1>
                  {firstPart}{" "}
                  <span className="italic" style={{ color: "var(--secondary)" }}>
                    {restPart}
                  </span>
                </h1>
              ) : (
                <h2>
                  {firstPart}{" "}
                  <span className="italic" style={{ color: "var(--secondary)" }}>
                    {restPart}
                  </span>
                </h2>
              )}
            </div>
            <p className="text-xs font-black uppercase tracking-[0.3em] text-gray-700">
              Legal Documentation / <span style={{ color: "var(--secondary)" }}>Boutique Protocols</span>
            </p>
          </motion.header>

          {/* --- ALIGNED CONTENT AREA --- */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Decor Bar (Architectural Element) */}
            <div className="hidden lg:block lg:col-span-1">
               <div className="sticky top-24 w-full h-1 bg-black mb-2" />
               <div className="sticky top-28 w-1/2 h-1" style={{ backgroundColor: "var(--secondary)" }} />
            </div>

            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-11 prose prose-neutral max-w-none"
            >
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: (props) => (
                    <h1 className="text-2xl font-black uppercase tracking-tighter border-b-4 border-black pb-2 mb-8" {...props} />
                  ),
                  h2: (props) => (
                    <h2 className="text-xl font-black uppercase tracking-tighter mt-12 mb-6 flex items-center gap-4">
                      <span className="h-4 w-1" style={{ backgroundColor: "var(--secondary)" }} />
                      {props.children}
                    </h2>
                  ),
                  p: (props) => (
                    <p className="text-neutral-600 leading-relaxed mb-6 font-medium" {...props} />
                  ),
                  ul: (props) => (
                    <ul className="list-none pl-0 mb-8 space-y-4" {...props} />
                  ),
                  li: ({ children, ...props }) => (
                    <li className="flex gap-4 items-start text-neutral-800 font-bold uppercase text-xs tracking-wide" {...props}>
                      <span className="mt-1" style={{ color: "var(--secondary)" }}>—</span>
                      <span>{children}</span>
                    </li>
                  ),
                  strong: (props) => (
                    <strong className="font-black text-black italic" {...props} />
                  ),
                  a: (props) => (
                    <a 
                      className="border-b-2 font-black uppercase tracking-widest text-[10px] transition-all hover:text-white" 
                      style={{ 
                        borderBottomColor: "var(--secondary)",
                        color: "black" 
                      }} 
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--secondary)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      {...props} 
                    />
                  ),
                  hr: () => (
                    <hr className="my-12 border-t-4 border-neutral-100" />
                  )
                }}
              >
                {data.content}
              </Markdown>
            </motion.div>
          </div>

          {/* Bottom Visual Progress Accent */}
          <div className="mt-20 flex items-center gap-4">
             <div className="h-1 bg-black flex-grow" />
             <p className="text-[11px] font-black uppercase tracking-[0.4em] whitespace-nowrap">
                Pure Elegance <span style={{ color: "var(--secondary)" }}>•</span> Crafted Perfection
             </p>
          </div>
        </div>
      </section>
    </>
  );
}